import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { NAV, LINKS, LOGO } from "../lib/site";

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const loc = useLocation();
  const solid = scrolled || loc.pathname !== "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [loc.pathname]);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        solid ? "bg-cream/80 backdrop-blur-xl backdrop-saturate-150 border-b border-ink-soft/10" : "bg-transparent"
      }`}
      data-testid="navbar"
    >
      <nav className="mx-auto max-w-7xl px-5 sm:px-8 h-16 sm:h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5 group" data-testid="nav-logo">
          <span className="h-14 w-14 shrink-0 transition-transform duration-300 group-hover:-rotate-6">
            <img src={LOGO} alt="Turkey Trot logo" className="h-14 w-14 object-contain" />
          </span>
          <span className="flex flex-col leading-none">
            <span className={`text-[9px] sm:text-[10px] uppercase tracking-[0.22em] font-medium transition-colors duration-300 ${solid ? "text-ink-soft" : "text-white/70"}`}>
              Grimsby On The Lake
            </span>
            <span className={`font-display text-xl sm:text-2xl font-semibold tracking-tight transition-colors duration-300 ${solid ? "text-ink" : "text-white"}`}>
              Turkey Trot
            </span>
          </span>
        </Link>

        <div className="hidden lg:flex items-center gap-6">
          {NAV.slice(1).map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              className={({ isActive }) =>
                `text-sm font-medium transition-colors duration-200 hover:text-terracotta ${
                  isActive ? "text-terracotta" : solid ? "text-ink-soft" : "text-white/85"
                }`
              }
              data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {n.label}
            </NavLink>
          ))}
        </div>

        <div className="hidden lg:flex items-center gap-3">
          <a
            href={LINKS.register}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm font-semibold px-5 py-2 rounded-full bg-terracotta text-white hover:bg-sienna transition-all duration-200 hover:scale-105 shadow-warm"
            data-testid="nav-register"
          >
            Register
          </a>
        </div>

        <button
          className={`lg:hidden p-2 transition-colors duration-200 ${solid ? "text-ink" : "text-white"}`}
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
          data-testid="nav-mobile-toggle"
        >
          {open ? <X /> : <Menu />}
        </button>
      </nav>

      {open && (
        <div className="lg:hidden bg-cream/95 backdrop-blur-xl border-t border-ink-soft/10 px-5 py-6" data-testid="mobile-menu">
          <div className="flex flex-col gap-1">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                className={({ isActive }) =>
                  `py-2 text-base font-medium ${isActive ? "text-terracotta" : "text-ink"}`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </div>
          <div className="flex gap-3 mt-5">
            <a href={LINKS.register} target="_blank" rel="noopener noreferrer" className="flex-1 text-center text-sm font-semibold px-4 py-3 rounded-full bg-terracotta text-white">Register</a>
          </div>
        </div>
      )}
    </header>
  );
};
